export const BUG_AI_PROMPT_VERSION = "bug-analysis-v1";

const decodeEntities = (value = "") =>
  String(value || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'");

const htmlToText = (value = "") => {
  const raw = String(value || "");
  if (!raw) return "";

  return decodeEntities(
    raw
      .replace(/<li[^>]*>/gi, "- ")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|ul|ol|h[1-6])>/gi, "\n")
      .replace(/<[^>]*>/g, "")
  )
    .replace(/\u00a0/g, " ")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n")
    .trim();
};

const clean = (value, fallback = "-") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

const getBugTitle = (bug = {}) =>
  clean(bug.title || bug.bug_title || bug.name || "Başlıksız bug");

const getBugDescription = (bug = {}) =>
  clean(htmlToText(bug.description || bug.bug_description || bug.detail || ""), "Detay girilmemiş.");

const getBugVersion = (bug = {}, fallback = "") =>
  clean(bug.version_name || bug.version || fallback, "-");

const pickCreatedBugData = (response = {}) => {
  if (response?.bug && typeof response.bug === "object") return response.bug;
  if (response?.data?.bug && typeof response.data.bug === "object") return response.data.bug;
  if (response?.data && typeof response.data === "object") return response.data;
  return response && typeof response === "object" ? response : {};
};

const getAttachmentLines = (bug = {}, attachments = []) => {
  const lines = [];
  const fileUrl = clean(bug.file_url || bug.fileUrl || "", "");
  const externalUrl = clean(bug.external_url || bug.externalUrl || "", "");

  if (fileUrl) lines.push(`- Dosya/Ekran görüntüsü: ${fileUrl}`);
  if (externalUrl) lines.push(`- Harici bağlantı: ${externalUrl}`);

  (attachments || []).forEach((item, index) => {
    const url = clean(item?.file_path || item?.file_url || item?.url || "", "");
    if (url) lines.push(`- Ek ${index + 1}: ${url}`);
  });

  return lines.length ? lines.join("\n") : "- Ek yok.";
};

export const buildBugAiPrompt = ({
  bug = {},
  projectId = "",
  projectName = "",
  versionName = "",
  attachments = [],
  currentUrl = "",
  prefix = ""
} = {}) => {
  const title = getBugTitle(bug);
  const description = getBugDescription(bug);
  const version = getBugVersion(bug, versionName);
  const status = bug.is_completed || bug.completed ? "Tamamlandı" : "Açık";
  const bugId = clean(bug.id || bug.bug_id || "", "-");
  const recordPrefix = clean(prefix, "");

  return [
    `Prompt sürümü: ${BUG_AI_PROMPT_VERSION}`,
    recordPrefix ? `Kayıt: ${recordPrefix}` : "",
    "",
    "Rol:",
    "Kıdemli bir QA mühendisi ve yazılım geliştirici gibi davran. Aşağıdaki bug kaydını analiz et, belirsiz noktaları açıkça belirt ve uygulanabilir bir çözüm planı çıkar.",
    "",
    "Bağlam:",
    `- Proje: ${clean(projectName, "Belirtilmedi")}`,
    `- Proje ID: ${clean(projectId, "-")}`,
    `- Sürüm: ${version}`,
    `- Bug ID: ${bugId}`,
    `- Durum: ${status}`,
    currentUrl ? `- Bug sayfası: ${currentUrl}` : "",
    "",
    "Bug kaydı:",
    `Başlık: ${title}`,
    "",
    "Açıklama:",
    description,
    "",
    "Ekler ve bağlantılar:",
    getAttachmentLines(bug, attachments),
    "",
    "İstenen çıktı formatı:",
    "1. Kısa özet: Bu bug ne anlatıyor?",
    "2. Eksik bilgi: Netleşmesi gereken sorular.",
    "3. Yeniden üretme adımları: Test eden kişinin izleyeceği adımlar.",
    "4. Olası kök nedenler: En muhtemel teknik sebepler.",
    "5. Çözüm planı: Öncelikli ve kontrollü geliştirme adımları.",
    "6. Test senaryoları: Manuel veya otomasyon testleri için kontrol listesi.",
    "7. Riskler: Bu bug düzeltilirken kırılabilecek alanlar.",
    "",
    "Kurallar:",
    "- Cevabı Türkçe ver.",
    "- Kod önerisi verirsen önce varsayımlarını yaz.",
    "- Bilgi yetersizse uydurma; hangi log, ekran görüntüsü veya adım gerektiğini söyle.",
    "- Gereksiz uzun anlatma, ekip içinde doğrudan kullanılabilir çıktı üret."
  ]
    .filter((line) => line !== "")
    .join("\n");
};

export const buildBugDevelopmentPrompt = ({
  bug = {},
  projectId = "",
  projectName = "",
  versionName = "",
  attachments = [],
  currentUrl = "",
  prefix = ""
} = {}) => {
  const title = getBugTitle(bug);
  const description = getBugDescription(bug);
  const version = getBugVersion(bug, versionName);
  const status = bug.is_completed || bug.completed ? "Tamamlandı" : "Açık";
  const bugId = clean(bug.id || bug.bug_id || "", "-");
  const recordPrefix = clean(prefix, "");

  return [
    `Prompt sürümü: ${BUG_AI_PROMPT_VERSION}`,
    recordPrefix ? `Kayıt: ${recordPrefix}` : "",
    "",
    "Rol:",
    "Kıdemli bir full-stack geliştirici ve teknik lider gibi davran. Aşağıdaki işi üretime yakın kalitede geliştirmek için uygulanabilir bir geliştirme promptu ve görev planı oluştur.",
    "",
    "Bağlam:",
    `- Proje: ${clean(projectName, "Belirtilmedi")}`,
    `- Proje ID: ${clean(projectId, "-")}`,
    `- Sürüm: ${version}`,
    `- Bug ID: ${bugId}`,
    `- Durum: ${status}`,
    currentUrl ? `- Bug sayfası: ${currentUrl}` : "",
    "",
    "İş kaydı:",
    `Başlık: ${title}`,
    "",
    "Açıklama:",
    description,
    "",
    "Ekler ve bağlantılar:",
    getAttachmentLines(bug, attachments),
    "",
    "İstenen çıktı formatı:",
    "1. Teknik özet: Bu iş hangi davranışı değiştirmeli?",
    "2. Kabul kriterleri: İş bitti sayılması için net maddeler.",
    "3. Uygulama planı: Dosya/modül bazlı kontrollü geliştirme adımları.",
    "4. Veri ve API etkisi: Backend, frontend, state veya validasyon etkileri.",
    "5. Kenar durumları: Kırılma ihtimali olan senaryolar.",
    "6. Test planı: Manuel ve otomasyon tarafında doğrulanacak maddeler.",
    "7. Teslim notu: PR veya ekip notu olarak kullanılabilecek kısa özet.",
    "",
    "Kurallar:",
    "- Cevabı Türkçe ver.",
    "- Belirsiz bilgi varsa varsayım üretmeden soru olarak yaz.",
    "- Gereksiz refactor önermeden mevcut yapıyı koruyan çözüm öner.",
    "- Çıktı doğrudan geliştirme ekibine verilebilir netlikte olsun."
  ]
    .filter((line) => line !== "")
    .join("\n");
};

export const buildBugTestScenarioPrompt = ({
  bug = {},
  projectId = "",
  projectName = "",
  versionName = "",
  attachments = [],
  currentUrl = "",
  prefix = ""
} = {}) => {
  const title = getBugTitle(bug);
  const description = getBugDescription(bug);
  const version = getBugVersion(bug, versionName);
  const status = bug.is_completed || bug.completed ? "Tamamlandı" : "Açık";
  const bugId = clean(bug.id || bug.bug_id || "", "-");
  const recordPrefix = clean(prefix, "");

  return [
    `Prompt sürümü: ${BUG_AI_PROMPT_VERSION}`,
    recordPrefix ? `Kayıt: ${recordPrefix}` : "",
    "",
    "Rol:",
    "Kıdemli bir QA mühendisi gibi davran. Aşağıdaki iş kaydından kapsamlı ama uygulanabilir test senaryoları üret.",
    "",
    "Bağlam:",
    `- Proje: ${clean(projectName, "Belirtilmedi")}`,
    `- Proje ID: ${clean(projectId, "-")}`,
    `- Sürüm: ${version}`,
    `- Bug ID: ${bugId}`,
    `- Durum: ${status}`,
    currentUrl ? `- Bug sayfası: ${currentUrl}` : "",
    "",
    "İş kaydı:",
    `Başlık: ${title}`,
    "",
    "Açıklama:",
    description,
    "",
    "Ekler ve bağlantılar:",
    getAttachmentLines(bug, attachments),
    "",
    "İstenen çıktı formatı:",
    "1. Test kapsamı: Hangi alanlar test edilmeli?",
    "2. Ön koşullar: Kullanıcı, veri, ortam ve yetki gereksinimleri.",
    "3. Pozitif senaryolar: Beklenen başarılı akışlar.",
    "4. Negatif senaryolar: Hatalı/veri eksikliği/izin kısıtı durumları.",
    "5. Regresyon senaryoları: Etkilenebilecek eski akışlar.",
    "6. Mobil/responsive kontrol: Gerekliyse cihaz ve viewport notları.",
    "7. Test verileri: Kullanılabilecek örnek değerler.",
    "8. Kabul kriterleri: Her senaryonun geçme koşulu.",
    "",
    "Kurallar:",
    "- Cevabı Türkçe ver.",
    "- Testleri tablo gibi okunabilir maddelerle yaz.",
    "- Bilgi eksikse senaryo uydurma; eksik bilgiyi ayrıca belirt.",
    "- Hem manuel test eden kişi hem otomasyon yazacak geliştirici kullanabilsin."
  ]
    .filter((line) => line !== "")
    .join("\n");
};

export const normalizeAddedBugForPrompt = ({
  response = {},
  form = {},
  versionName = "",
  fileUrl = ""
} = {}) => {
  const createdBug = pickCreatedBugData(response);
  const id =
    createdBug.id ||
    createdBug.bug_id ||
    response?.id ||
    response?.bug_id ||
    response?.data?.id ||
    "";

  return {
    ...createdBug,
    id,
    bug_id: createdBug.bug_id || id,
    title: createdBug.title || createdBug.bug_title || form.title || "",
    description: createdBug.description || createdBug.bug_description || form.description || "",
    file_url: createdBug.file_url || createdBug.fileUrl || fileUrl || form.fileUrl || "",
    external_url: createdBug.external_url || createdBug.externalUrl || form.externalUrl || "",
    version_name: createdBug.version_name || createdBug.version || form.version || versionName,
    is_completed: createdBug.is_completed || createdBug.completed || false
  };
};

export const buildAddedBugAiPrompt = ({
  response = {},
  form = {},
  projectId = "",
  projectName = "",
  versionName = "",
  fileUrl = "",
  currentUrl = ""
} = {}) =>
  buildBugAiPrompt({
    bug: normalizeAddedBugForPrompt({ response, form, versionName, fileUrl }),
    projectId,
    projectName,
    versionName,
    currentUrl
  });

export const buildBugPromptFromForm = ({
  form = {},
  projectId = "",
  projectName = "",
  versionName = "",
  currentUrl = ""
} = {}) =>
  buildBugAiPrompt({
    bug: {
      title: form.title,
      description: form.description,
      file_url: form.fileUrl,
      external_url: form.externalUrl,
      version_name: form.version || versionName,
      id: form.id
    },
    projectId,
    projectName,
    versionName,
    currentUrl
  });

export const buildBugDevelopmentPromptFromForm = ({
  form = {},
  projectId = "",
  projectName = "",
  versionName = "",
  currentUrl = ""
} = {}) =>
  buildBugDevelopmentPrompt({
    bug: {
      title: form.title,
      description: form.description,
      file_url: form.fileUrl,
      external_url: form.externalUrl,
      version_name: form.version || versionName,
      id: form.id
    },
    projectId,
    projectName,
    versionName,
    currentUrl
  });

export const buildBugTestScenarioPromptFromForm = ({
  form = {},
  projectId = "",
  projectName = "",
  versionName = "",
  currentUrl = ""
} = {}) =>
  buildBugTestScenarioPrompt({
    bug: {
      title: form.title,
      description: form.description,
      file_url: form.fileUrl,
      external_url: form.externalUrl,
      version_name: form.version || versionName,
      id: form.id
    },
    projectId,
    projectName,
    versionName,
    currentUrl
  });
